import { useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import Warning from "@mui/icons-material/Warning";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { VerifiedFacilitiesForPilotQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";

// Query to get PROCESSING facilities for a state (excludes denied tenants)
const VERIFIED_FACILITIES_QUERY = gql(`
  query VerifiedFacilitiesForPilot($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!], $excludeDeniedTenants: Boolean) {
    chemicalFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort, excludeDeniedTenants: $excludeDeniedTenants) {
      items {
        id
        facilityId
        tierIIReportId
        organization
        facilityName
        streetAddress1
        city
        state
        customerFacilityId
        organizationStatus
        encampStatus
        reportKind
        partnerTenantName
        chemicalCount
        issues
        workflowStatus
      }
      count
    }
  }
`);

const ADD_FACILITY_TO_PILOT_PROGRAM_MUTATION = gql(`
  mutation AddFacilityToPilotProgram($facilityId: ID!) {
    addFacilityToPilotProgram(facilityId: $facilityId) {
      id
      type
      value
      notes
    }
  }
`);

const ADD_FACILITY_TO_PILOT_PROGRAM_LOCAL_MUTATION = gql(`
  mutation AddFacilityToPilotProgramLocal($facilityId: ID!, $localJurisdictionName: String!) {
    addFacilityToPilotProgramLocal(facilityId: $facilityId, localJurisdictionName: $localJurisdictionName) {
      id
      type
      value
      notes
    }
  }
`);

// Query to get DENY_TENANT and DENY_TENANT_FOR_STATE entries from WorkflowManagerConfig
const DENY_TENANT_CONFIGS_QUERY = gql(`
  query DenyTenantConfigs($search: String) {
    workflowManagerConfigRules(search: $search, page: 1, pageSize: 1000) {
      items {
        id
        type
        value
      }
    }
  }
`);

// Query to get tenant names for denied tenant IDs
const DENIED_TENANTS_QUERY = gql(`
  query DeniedTenants($search: String) {
    tenants(search: $search, page: 1, pageSize: 1000) {
      items {
        id
        name
      }
    }
  }
`);

// Query to get pilot facilities with isCohort information
const FACILITY_PILOT_PROGRAM_STATES_QUERY = gql(`
  query FacilityPilotProgramStatesForModal($reportingYear: Int!) {
    facilityPilotProgramStates(reportingYear: $reportingYear) {
      items {
        pilotFacilities {
          facilityId
          isCohort
        }
      }
    }
  }
`);

type FacilityRow = VerifiedFacilitiesForPilotQuery["chemicalFacilities"]["items"][number];

interface AddPilotFacilityModalProps {
  open: boolean;
  onClose: () => void;
  onFacilityAdded: () => void;
  state: string | null;
  reportingYear: number;
  existingPilotFacilityIds: Set<string>;
  localJurisdictionName?: string;
  jurisdictionType?: string | null;
  jurisdictionMatchKey?: string | null;
}

export function AddPilotFacilityModal({
  open,
  onClose,
  onFacilityAdded,
  state,
  reportingYear,
  existingPilotFacilityIds,
  localJurisdictionName,
  jurisdictionType,
  jurisdictionMatchKey,
}: AddPilotFacilityModalProps) {
  const alerts = useAlerts();
  const [addingFacilityId, setAddingFacilityId] = useState<string | null>(null);

  // defaultSearch contains hidden filters that are always applied but not shown in the omnisearch bar
  const defaultSearch = useMemo(() => {
    return `reportingYear:${reportingYear} encampStatus:PROCESSING reportKind:ANNUAL`;
  }, [reportingYear]);

  // initialFilters contains visible filters that appear in the omnisearch bar and can be modified by the user
  const initialFilters = useMemo(() => {
    const filters: { field: string; value: string }[] = [];

    // State filter is always added and visible
    if (state) {
      filters.push({ field: "state", value: state });
    }

    // Add jurisdiction-specific filter for local jurisdictions
    // These filters are visible in the omnisearch bar and can be cleared/modified by the user
    if (localJurisdictionName && jurisdictionType && jurisdictionMatchKey) {
      switch (jurisdictionType) {
        case "City":
        case "Township":
          filters.push({ field: "city", value: jurisdictionMatchKey });
          break;
        case "County":
          filters.push({ field: "county", value: jurisdictionMatchKey });
          break;
        case "FireDepartment":
          filters.push({
            field: "fireDepartment",
            value: jurisdictionMatchKey,
          });
          break;
        case "LEPC":
          filters.push({ field: "lepc", value: jurisdictionMatchKey });
          break;
        // For "Other" type, we show all facilities in the state
        // and let the user select the appropriate ones
      }
    }

    return filters;
  }, [state, localJurisdictionName, jurisdictionType, jurisdictionMatchKey]);

  // Query DENY_TENANT entries from WorkflowManagerConfig
  const { data: denyTenantConfigsData } = useQuery(DENY_TENANT_CONFIGS_QUERY, {
    variables: {
      search: `type:DENY_TENANT`,
    },
    skip: !open,
  });

  // Query DENY_TENANT_FOR_STATE entries from WorkflowManagerConfig
  const { data: denyTenantForStateConfigsData } = useQuery(DENY_TENANT_CONFIGS_QUERY, {
    variables: {
      search: `type:DENY_TENANT_FOR_STATE`,
    },
    skip: !open,
  });

  // Get denied tenant IDs from both global and state-specific configs
  const deniedTenantIds = useMemo(() => {
    const deniedIds = new Set<string>();

    // Add globally denied tenants
    const globalItems = denyTenantConfigsData?.workflowManagerConfigRules?.items ?? [];
    for (const config of globalItems) {
      deniedIds.add(config.value);
    }

    // Add state-specific denied tenants
    // Key format: "tenantId:stateAbbreviation" - see buildTenantStateKey in encamp-workflows/src/utils.ts
    if (state) {
      const stateItems = denyTenantForStateConfigsData?.workflowManagerConfigRules?.items ?? [];
      for (const config of stateItems) {
        const [tenantId, configState] = config.value.split(":");
        if (!tenantId || !configState) {
          console.warn(
            `Invalid DENY_TENANT_FOR_STATE config value: "${config.value}" (expected format: tenantId:stateAbbreviation)`
          );
          continue;
        }
        if (configState === state) {
          deniedIds.add(tenantId);
        }
      }
    }

    return deniedIds;
  }, [denyTenantConfigsData, denyTenantForStateConfigsData, state]);

  // Query tenant names for denied tenant IDs
  const deniedTenantIdsArray = useMemo(() => Array.from(deniedTenantIds), [deniedTenantIds]);

  // Build search string for tenant IDs
  const deniedTenantsSearch = useMemo(() => {
    if (deniedTenantIdsArray.length === 0) return "";
    // Join multiple IDs with | separator (consistent with other queries in the codebase)
    return `ids:${deniedTenantIdsArray.join("|")}`;
  }, [deniedTenantIdsArray]);

  const { data: deniedTenantsData } = useQuery(DENIED_TENANTS_QUERY, {
    variables: {
      search: deniedTenantsSearch,
    },
    skip: !open || deniedTenantIdsArray.length === 0,
  });

  // Get denied tenant names to match against organization field
  const deniedTenantNames = useMemo(() => {
    const items = deniedTenantsData?.tenants?.items ?? [];
    return new Set(items.map((tenant: { name: string }) => tenant.name));
  }, [deniedTenantsData]);

  // Query pilot facilities to get isCohort information
  const { data: pilotFacilitiesData } = useQuery(FACILITY_PILOT_PROGRAM_STATES_QUERY, {
    variables: {
      reportingYear,
    },
    skip: !open,
  });

  // Create a map of facilityId -> isCohort from pilot facilities
  const facilityIsCohortMap = useMemo(() => {
    const map = new Map<string, boolean>();
    const items = pilotFacilitiesData?.facilityPilotProgramStates?.items ?? [];
    for (const stateItem of items) {
      for (const pilotFacility of stateItem.pilotFacilities ?? []) {
        if (pilotFacility.facilityId) {
          map.set(pilotFacility.facilityId, pilotFacility.isCohort ?? false);
        }
      }
    }
    return map;
  }, [pilotFacilitiesData]);

  const [addMutation, { loading: addLoading }] = useMutation(
    ADD_FACILITY_TO_PILOT_PROGRAM_MUTATION,
    {
      onCompleted: () => {
        alerts.success("Facility added to pilot program");
        setAddingFacilityId(null);
        onFacilityAdded();
      },
      onError: (error) => {
        alerts.error(`Failed to add facility: ${error.message}`);
        setAddingFacilityId(null);
      },
    }
  );

  const [addLocalMutation, { loading: addLocalLoading }] = useMutation(
    ADD_FACILITY_TO_PILOT_PROGRAM_LOCAL_MUTATION,
    {
      onCompleted: () => {
        alerts.success("Facility added to local jurisdiction pilot program");
        setAddingFacilityId(null);
        onFacilityAdded();
      },
      onError: (error) => {
        alerts.error(`Failed to add facility: ${error.message}`);
        setAddingFacilityId(null);
      },
    }
  );

  const handleAddFacility = useCallback(
    (facilityId: string, facilityName: string, organization?: string | null) => {
      // Prevent adding if already in progress or already a pilot
      if (addingFacilityId || existingPilotFacilityIds.has(facilityId)) {
        return;
      }

      // Check if the organization (tenant name) is denied
      const isDenied = organization ? deniedTenantNames.has(organization) : false;
      if (isDenied) {
        const confirmed = window.confirm(
          `Warning: The tenant for "${facilityName}" is on the DENY list. The workflow will not run until the tenant denial is removed.\n\nDo you still want to add this facility to the pilot program?`
        );
        if (!confirmed) return;
      }

      setAddingFacilityId(facilityId);

      if (localJurisdictionName) {
        addLocalMutation({
          variables: {
            facilityId,
            localJurisdictionName,
          },
        });
      } else {
        addMutation({
          variables: {
            facilityId,
          },
        });
      }
    },
    [
      addMutation,
      addLocalMutation,
      addingFacilityId,
      existingPilotFacilityIds,
      deniedTenantNames,
      localJurisdictionName,
    ]
  );

  const columns: OmnisearchGridColDef<FacilityRow>[] = useMemo(
    () => [
      {
        field: "isPilot",
        headerName: "Is Pilot",
        width: 80,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const isPilot = row.facilityId ? existingPilotFacilityIds.has(row.facilityId) : false;
          return (
            <Stack justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
              {isPilot && <Check color="success" />}
            </Stack>
          );
        },
      },
      {
        field: "facilityName",
        headerName: "Facility",
        flex: 2,
        minWidth: 280,
        renderCell: ({ row }) => (
          <Stack justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="body2">{row.facilityName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[row.streetAddress1, row.city, row.state].filter(Boolean).join(", ")}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "organization",
        headerName: "Organization",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => {
          const isDenied = row.organization ? deniedTenantNames.has(row.organization) : false;
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              spacing={1}
              sx={{ height: "100%" }}
            >
              <Typography variant="body2">{row.organization}</Typography>
              {isDenied && (
                <Tooltip title="Tenant is on the DENY list - workflow will not run">
                  <Warning color="error" fontSize="small" />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        field: "customerFacilityId",
        headerName: "Facility ID",
        width: 120,
      },
      {
        field: "chemicalCount",
        headerName: "Chemicals",
        width: 100,
        type: "number",
        renderCell: ({ row }) => (
          <Stack justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="body2">{row.chemicalCount ?? 0}</Typography>
          </Stack>
        ),
      },
      {
        field: "isCohort",
        headerName: "Is Cohort",
        width: 100,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const isCohort = row.facilityId
            ? (facilityIsCohortMap.get(row.facilityId) ?? false)
            : false;
          return (
            <Stack justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
              {isCohort && <Check color="success" />}
            </Stack>
          );
        },
      },
      {
        field: "workflowStatus",
        headerName: "Workflow Status",
        width: 100,
        renderCell: ({ row }) => {
          if (!row.workflowStatus) {
            return (
              <Stack justifyContent="center" sx={{ height: "100%" }}>
                <Typography variant="body2" color="text.secondary">
                  Not Started
                </Typography>
              </Stack>
            );
          }
          const color =
            row.workflowStatus === "Completed"
              ? "success"
              : row.workflowStatus === "Error"
                ? "error"
                : row.workflowStatus === "InProgress"
                  ? "info"
                  : "default";
          return (
            <Chip label={prettyPrintEnumValue(row.workflowStatus)} size="small" color={color} />
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const isPilot = row.facilityId ? existingPilotFacilityIds.has(row.facilityId) : false;
          if (isPilot) return null;
          const isAdding = row.facilityId === addingFacilityId;
          return (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() =>
                handleAddFacility(row.facilityId ?? "", row.facilityName ?? "", row.organization)
              }
              disabled={isAdding || addLoading || addLocalLoading || !row.facilityId}
            >
              {isAdding ? "Adding..." : "Add"}
            </Button>
          );
        },
      },
    ],
    [
      deniedTenantNames,
      existingPilotFacilityIds,
      handleAddFacility,
      addLoading,
      addLocalLoading,
      addingFacilityId,
      facilityIsCohortMap,
    ]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        localJurisdictionName
          ? `Add Pilot Facility for ${localJurisdictionName}, ${state}`
          : `Add Pilot Facility for ${state}`
      }
      maxWidth="lg"
      fullWidth
      hideCancelButton
      primaryActionButton={
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      }
    >
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {localJurisdictionName
              ? `Select a facility with a PROCESSING report for ${reportingYear} to add to the ${localJurisdictionName} local jurisdiction pilot program. Facilities from tenants on the DENY list are marked with a warning.`
              : `Select a facility with a PROCESSING report for ${reportingYear} to add to the pilot program. Facilities from tenants on the DENY list are marked with a warning.`}
          </Typography>

          <OmnisearchDataGrid<
            VerifiedFacilitiesForPilotQuery,
            {
              excludeDeniedTenants?: boolean;
            },
            FacilityRow
          >
            persistenceKey="add-pilot-facility-modal"
            columns={columns}
            dataQuery={VERIFIED_FACILITIES_QUERY}
            skip={!open || !state}
            defaultSearch={defaultSearch}
            initialFilters={initialFilters}
            dataQueryVariables={{ excludeDeniedTenants: true }}
            getItems={(data) => {
              const items = data.chemicalFacilities.items;
              // Denied tenants are filtered server-side to match FPP verifiedReportCount
              // Only exclude items without facilityId or organization (defensive check)
              return items.filter((item) => {
                return item.facilityId && item.organization;
              });
            }}
            getCount={(data) => {
              // Use the server's total count for pagination
              // Denied tenants are filtered server-side, so this count matches
              // the FPP datagrid's verifiedReportCount and the displayed items
              return data.chemicalFacilities.count ?? 0;
            }}
            commandButtons={[]}
            noDataMessage={`No PROCESSING reports found for ${state} in ${reportingYear}.`}
            isURLDriven={false}
            withPadding={false}
            initialPageSize={25}
            initialSortModel={[{ field: "facilityName", sort: "asc" }]}
            columnVisibilityModel={{ organizationStatus: false }}
            showManageColumns={false}
            gridSx={{
              minHeight: "400px",
              "& .MuiDataGrid-cell": {
                py: 1,
              },
            }}
            rowHeight={56}
            disableRowSelectionOnClick
            additionalFilterColumns={[
              { header: "Facility ID", key: "customerFacilityId" },
              { header: "City", key: "city" },
              { header: "State", key: "state" },
              { header: "County", key: "county" },
              { header: "LEPC", key: "lepc" },
              { header: "Fire Department", key: "fireDepartment" },
              { header: "Organization", key: "organization" },
            ]}
          />
        </Stack>
      </Box>
    </Dialog>
  );
}
